import {
  LayersControl,
  MapContainer,
  Polygon,
  Popup,
  TileLayer,
  ZoomControl,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import {
  DrawControl,
  FitMapPosition,
  FitPolygonBounds,
  getPolygonColor,
  MAIN_POLYGON_PATH_OPTIONS,
  normalizeMapPosition,
  SearchControl,
  tileLayers,
  MarkerDrawControl,
} from "./mapUtils";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import MapMarkerCluster from "./mapMarkerCluster.component";
import { useMemo, useState } from "react";
import FieldStatusControl from "./mapFieldStatusControl.component";
import SensorHeatmapLayer from "./sensorHeatmapLayer.component";
import SensorHeatmapMarkers from "./sensorHeatmapMarkers.component";
import MapDiseaseMarkerCluster from "./mapDiseaseMarkerCluster.component";
import PmtilesLayer from "./pmtilesLayer.component";
import {
  filterItemsWithinPolygon,
  normalizePolygonCoordinates,
} from "../../utils/geoUtils";
import { FIELD_STATUS_ALL_CLASSIFICATIONS } from "../../utils/fieldDataDiseaseStatusUtils";
delete L.Icon.Default.prototype._getIconUrl;

L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const defaultPosition = [40.72587098589281, 8.555417353048492];

const Map = ({
  fieldPosition,
  polygonCoordinates,
  onPolygonCreated,
  onPolygonDraftChange,
  onPolygonDeleted,
  allowPolygonDelete = true,
  isDrawable = false,
  zoomLevel,
  subPolygonCoordinates,
  mode = "main",
  showZoomControl = true,
  showLayersControl = true,
  subAreas,
  markers,
  showFieldStatusControl = false,
  isFieldStatusEnabled = false,
  onFieldStatusEnabledChange,
  fieldStatusClassification,
  onFieldStatusClassificationChange,
  diseaseStatusImages,
  diseaseStatusClassificationCounts,
  fieldNoteDiseaseReports,
  onDiseaseImageClick,
  demoMapZoom,
  isMarkerMode = false,
  onMarkerCreated,
  markerPosition,
  isMarkerReadOnly = false,
  pmtilesUrl,
  heatmap,
}) => {
  const [heatmapOpacity, setHeatmapOpacity] = useState(0.7);
  const [heatmapRenderState, setHeatmapRenderState] = useState({ status: "idle" });
  const heatmapData =
    heatmap?.enabled && heatmap.status === "ready" ? heatmap.data : null;
  const heatmapStatus = heatmapData
    ? heatmapRenderState.requestId === heatmapData.requestId
      ? heatmapRenderState.status
      : "loading"
    : heatmap?.status;
  const normalizedFieldPosition = normalizeMapPosition(fieldPosition);
  const position = normalizedFieldPosition || defaultPosition;
  const normalizedPolygonCoordinates = useMemo(
    () => normalizePolygonCoordinates(polygonCoordinates),
    [polygonCoordinates],
  );
  const normalizedDrawPolygonCoordinates = useMemo(
    () =>
      normalizePolygonCoordinates(
        mode === "sub" ? subPolygonCoordinates : polygonCoordinates,
      ),
    [mode, polygonCoordinates, subPolygonCoordinates],
  );
  const hasPolygonCoordinates = normalizedPolygonCoordinates.length >= 3;
  const normalizedSubAreas = useMemo(
    () =>
      (Array.isArray(subAreas) ? subAreas : [])
        .map((subArea) => {
          const savedPolygon = normalizePolygonCoordinates(subArea?.polygon);
          const polygon =
            savedPolygon.length >= 3
              ? savedPolygon
              : normalizePolygonCoordinates(
                  subArea?.subPolygonCoordinates,
                );

          return polygon.length >= 3 ? { ...subArea, polygon } : null;
        })
        .filter(Boolean),
    [subAreas],
  );

  const classificationOptions = useMemo(
    () =>
      Object.entries(diseaseStatusClassificationCounts || {})
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([classification, count]) => ({
          value: classification,
          label: `${classification} (${count})`,
        })),
    [diseaseStatusClassificationCounts],
  );
  const visibleDiseaseStatusImages = useMemo(
    () => filterItemsWithinPolygon(diseaseStatusImages, polygonCoordinates),
    [diseaseStatusImages, polygonCoordinates],
  );
  const visibleFieldNoteDiseaseReports = useMemo(() => {
    const reports = filterItemsWithinPolygon(
      fieldNoteDiseaseReports,
      polygonCoordinates,
    );

    if (
      !fieldStatusClassification ||
      fieldStatusClassification === FIELD_STATUS_ALL_CLASSIFICATIONS
    ) {
      return reports;
    }

    return reports.filter(
      (report) => report?.disease?.name === fieldStatusClassification,
    );
  }, [
    fieldNoteDiseaseReports,
    fieldStatusClassification,
    polygonCoordinates,
  ]);
  return (
    <>
      <MapContainer
        center={position}
        zoom={zoomLevel || 16}
        maxZoom={21}
        style={{ height: "100%", width: "100%", zIndex: 0 }}
        scrollWheelZoom={false}
        zoomControl={false}
      >
        {hasPolygonCoordinates && (
          <FitPolygonBounds
            polygonCoordinates={normalizedPolygonCoordinates}
            maxZoom={demoMapZoom}
          />
        )}
        {!hasPolygonCoordinates && normalizedFieldPosition && (
          <FitMapPosition
            position={normalizedFieldPosition}
            zoom={zoomLevel || 16}
          />
        )}
        {showZoomControl && <ZoomControl position="bottomright" />}

        {showLayersControl && (
          <LayersControl position="bottomleft">
            {Object.keys(tileLayers).map((key, index) => (
              <LayersControl.BaseLayer
                key={key}
                name={tileLayers[key].name}
                checked={index === 0}
              >
                <TileLayer
                  url={tileLayers[key].url}
                  attribution={tileLayers[key].attribution}
                  maxZoom={tileLayers[key].maxZoom || 18}
                />
              </LayersControl.BaseLayer>
            ))}
          </LayersControl>
        )}
        {!showLayersControl && (
          <TileLayer
            url={tileLayers[Object.keys(tileLayers)[0]].url}
            attribution={tileLayers[Object.keys(tileLayers)[0]].attribution}
          />
        )}
        {pmtilesUrl && (
          <PmtilesLayer
            url={pmtilesUrl}
            zIndex={350}
            fitToBounds={!hasPolygonCoordinates}
          />
        )}
        {!(isDrawable && mode === "main") && hasPolygonCoordinates && (
          <Polygon
            positions={normalizedPolygonCoordinates}
            pathOptions={
              heatmap?.enabled
                ? { ...MAIN_POLYGON_PATH_OPTIONS, fillOpacity: 0 }
                : MAIN_POLYGON_PATH_OPTIONS
            }
          />
        )}
        {showFieldStatusControl && (
          <FieldStatusControl
            isEnabled={isFieldStatusEnabled}
            onEnabledChange={onFieldStatusEnabledChange}
            classification={fieldStatusClassification}
            onClassificationChange={onFieldStatusClassificationChange}
            classificationOptions={classificationOptions}
            heatmap={heatmap ? {
              ...heatmap,
              status: heatmapStatus,
              error: heatmapStatus === "error" && heatmapData
                ? heatmapRenderState.error
                : heatmap.error,
            } : undefined}
            opacity={heatmapOpacity}
            onOpacityChange={setHeatmapOpacity}
          />
        )}
        {heatmapData && (
          <SensorHeatmapLayer
            data={heatmapData}
            opacity={heatmapOpacity}
            onStatusChange={setHeatmapRenderState}
          />
        )}
        {heatmapData && heatmapStatus === "ready" && (
          <SensorHeatmapMarkers data={heatmapData} sensors={markers} />
        )}
        {!isFieldStatusEnabled && !heatmap?.enabled && markers?.length > 0 && (
          <MapMarkerCluster
            markers={markers}
            polygonCoordinates={normalizedPolygonCoordinates}
            position={position}
          />
        )}
        {isFieldStatusEnabled && (
          <MapDiseaseMarkerCluster
            images={visibleDiseaseStatusImages}
            reports={visibleFieldNoteDiseaseReports}
            onImageClick={onDiseaseImageClick}
          />
        )}

        {normalizedSubAreas.map((subArea, index) => (
          <Polygon
            key={index}
            positions={subArea.polygon}
            pathOptions={{
              color: getPolygonColor(index),
              fillOpacity: heatmap?.enabled ? 0 : 0.2,
            }}
          >
            <Popup>
              <div>
                <div>
                  <strong>Nome:</strong>
                </div>
                <div style={{ marginBottom: "4px" }}>
                  {subArea?.name || subArea?.subAreaName}
                </div>
                <div>
                  <strong>Tipo:</strong>
                </div>
                <div>{subArea?.field_type?.name || subArea?.subAreaType}</div>
              </div>
            </Popup>
          </Polygon>
        ))}
        {isDrawable && (
          <DrawControl
            onPolygonCreated={onPolygonCreated}
            onPolygonEdited={onPolygonCreated}
            onPolygonDraftChange={onPolygonDraftChange}
            onPolygonDeleted={onPolygonDeleted}
            allowPolygonDelete={allowPolygonDelete}
            polygonCoordinates={normalizedDrawPolygonCoordinates}
            subAreas={subAreas}
            mode={mode}
          />
        )}
        {isDrawable && <SearchControl />}
        {isMarkerMode && (
          <MarkerDrawControl
            onMarkerCreated={onMarkerCreated}
            markerPosition={markerPosition}
            isReadOnly={isMarkerReadOnly}
          />
        )}
      </MapContainer>
    </>
  );
};

export default Map;
